export type UrlParts = {
    pathname: string;
    search?: string;
    searchParams?: SearchParams;
    hash?: string;
    origin?: string;
};

type UrlParams<UrlRouteParams extends RouteParams, UrlSearchParams extends SearchParams> = {
    params: UrlRouteParams;
    searchParams?: UrlSearchParams;
    hash?: string;
};

type RouteParams = Record<string, string | number>;

export type SearchParams = Record<string, SearchParamsValue | SearchParamsValue[]>;

type SearchParamsValue = string | number | null | undefined;

type RequiredKeys<T> = {
    [K in keyof T]-?: {} extends Pick<T, K> ? never : K;
}[keyof T];

const baseAbsoluteUrl = 'https://yandex.ru';

export function buildRoute<
    UrlRouteParams extends RouteParams = never,
    UrlSearchParams extends SearchParams = SearchParams,
    Route extends string = string,
>(route: Route) {
    return {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        getUrl: (({ params, searchParams, hash }: Partial<UrlParams<RouteParams, UrlSearchParams>> = {}) => {
            return makeUrl({
                pathname: generatePath(route, params),
                searchParams: searchParams,
                hash: hash,
            });
        }) as RequiredKeys<UrlRouteParams> extends never
            ? (arg?: Partial<UrlParams<UrlRouteParams, UrlSearchParams>>) => string
            : (arg: UrlParams<UrlRouteParams, UrlSearchParams>) => string,
        route,
    };
}

export function makeUrl({ hash, pathname, search, searchParams, origin }: UrlParts) {
    const url = new URL(pathname, origin ?? baseAbsoluteUrl);

    if (search) {
        url.search = search;
    }

    if (!search && searchParams) {
        url.search = makeSearchString(searchParams);
    }

    if (hash) {
        url.hash = hash;
    }

    if (origin) {
        return url.toString();
    }

    return url.pathname + url.search + url.hash;
}

export function makeSearchString(searchParams: SearchParams) {
    const search = [];

    for (const [name, value] of Object.entries(searchParams)) {
        if (value === undefined || value === null) {
            continue;
        }

        if (Array.isArray(value)) {
            for (const item of value) {
                if (item === undefined || item === null) {
                    continue;
                }
                search.push(stringifyParam(name, item));
            }
        } else {
            search.push(stringifyParam(name, value));
        }
    }

    return search.join('&');
}

function stringifyParam(key: string, value: string | number) {
    return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
}

// @see https://reactrouter.com/docs/en/v6/api#generatepath
function generatePath(route: string, params: RouteParams = {}) {
    return route
        .replace(/:(\w+)/g, (_, key) => {
            return params[key] === undefined ? `:${key}` : String(params[key]);
        })
        .replace(/\/*\*$/, _ => {
            const value = params['*'];

            if (value === undefined) {
                return '';
            }

            return String(params['*']).replace(/^\/*/, '/');
        });
}
